/**
 * WCC360 v2 — "What lights you up".
 *
 * This is the one screen in the walk that asks a learner directly what they
 * care about, and it is safe to ask directly *because it is a preference,
 * not a virtue*. "I'm drawn to wildlife" has no socially correct answer.
 * Nothing here is a hard filter and nothing here can delete a career from
 * the pool — see `passions.ts` for exactly how bounded the effect is.
 *
 * Interaction: category accordion, tap to pick, hard cap of MAX_PASSIONS, an
 * explicit "none of these" escape that is honoured rather than scored as a
 * midpoint, and an explicit Continue like every other screen.
 */
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, X } from "lucide-react";
import { PASSION_CATEGORIES, PASSIONS_BY_CATEGORY, NONE_PASSION_ID } from "@/data/passions";
import { MAX_PASSIONS, CATEGORY_LIMITS, passionById, pickBlock } from "../passions";
import { useAssessmentV2 } from "../store";
import { useScreenDraft } from "../useScreenDraft";

import { cn } from "@/lib/utils";

const COPY: Record<string, { title: string; subtitle: string }> = {
  "12to14": {
    title: "What lights you up?",
    subtitle:
      "Tap to pick up to 7 things you love doing — in or out of school (up to 3 causes and 4 free-time activities). Tap a pick again, or its ×, to remove it. There's no wrong pick, and you can change your mind later.",
  },
  "15to18": {
    title: "What lights you up?",
    subtitle:
      "Tap to pick up to 7 — at most 3 causes and 4 free-time activities. Tap a pick again, or its ×, to remove it. This isn't about what you're good at, or what looks impressive — just what you'd choose on a free afternoon.",
  },
  "18plus": {
    title: "What lights you up?",
    subtitle:
      "Tap to pick up to 7 — at most 3 causes and 4 free-time activities. Tap a pick again, or its ×, to remove it. Not what you're qualified in — what you'd still be interested in if nobody were paying you.",
  },
};

interface Props { onNext: () => void }

export function PassionsScreen({ onNext }: Props) {
  const { state, setPart1, band } = useAssessmentV2();
  const reduce = useReducedMotion();
  const copy = COPY[band] ?? COPY["18plus"];

  const saved = state.part1.passion_ids;
  // Picks survive navigating away: they live as a draft until Continue.
  const [draft, setDraft, clearDraft] = useScreenDraft<{ none: boolean; selected: string[] }>(
    band,
    "px_passions",
    { none: false, selected: [] },
    saved
      ? {
          none: saved.includes(NONE_PASSION_ID),
          selected: saved.filter((id) => id !== NONE_PASSION_ID),
        }
      : null,
  );
  const { none, selected } = draft;
  const [openCat, setOpenCat] = useState<string | null>(PASSION_CATEGORIES[0]);

  const full = selected.length >= MAX_PASSIONS;
  const canContinue = none || selected.length > 0;

  const toggle = (id: string) => {
    setDraft((prev) => ({
      none: false,
      selected: prev.selected.includes(id)
        ? prev.selected.filter((x) => x !== id)
        : pickBlock(prev.selected, id)
          ? prev.selected
          : [...prev.selected, id],
    }));
  };

  const commit = () => {
    setPart1({ passion_ids: none ? [NONE_PASSION_ID] : selected });
    clearDraft();
    onNext();
  };

  const chips = useMemo(
    () => selected.map((id) => passionById(id)).filter(Boolean),
    [selected],
  );


  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h2 className="font-display text-2xl md:text-3xl font-medium leading-snug">
        {copy.title}
      </h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">{copy.subtitle}</p>

      <p className="mt-4 text-xs uppercase tracking-[0.16em] text-primary" aria-live="polite">
        {none
          ? "None chosen — that's completely fine"
          : `${selected.length} of ${MAX_PASSIONS} chosen`}
      </p>

      {full && !none && (
        <p role="status" className="mt-3 rounded-2xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">
          You've used all {MAX_PASSIONS} picks — remove one to choose something else.
        </p>
      )}

      {chips.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {chips.map((p) => (
            <li key={p!.id}>
              <button
                type="button"
                onClick={() => toggle(p!.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/40
                  bg-primary/10 px-3 py-1.5 text-sm text-foreground
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {p!.label}
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Remove {p!.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 space-y-3">
        {PASSION_CATEGORIES.map((cat) => {
          const items = PASSIONS_BY_CATEGORY[cat] ?? [];
          const isOpen = openCat === cat;
          const chosen = items.filter((i) => selected.includes(i.id)).length;
          return (
            <div key={cat} className="rounded-3xl border border-border bg-card overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenCat(isOpen ? null : cat)}
                aria-expanded={isOpen}
                className="w-full min-h-[56px] px-5 py-4 flex items-center justify-between gap-3
                  text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="min-w-0">
                  <span className="block font-medium">{cat}</span>
                  {CATEGORY_LIMITS[cat] !== undefined && (
                    <span className="block text-xs text-muted-foreground">
                      Pick up to {CATEGORY_LIMITS[cat]}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2 text-muted-foreground">
                  {chosen > 0 && (
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
                      {chosen}
                    </span>
                  )}
                  <ChevronDown
                    className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
                    aria-hidden="true"
                  />
                </span>
              </button>

              {isOpen && (
                <motion.ul
                  initial={reduce ? undefined : { opacity: 0, height: 0 }}
                  animate={reduce ? undefined : { opacity: 1, height: "auto" }}
                  className="px-3 pb-3 grid gap-2 sm:grid-cols-2"
                >
                  {CATEGORY_LIMITS[cat] !== undefined && chosen >= CATEGORY_LIMITS[cat] && (
                    <li role="status" className="sm:col-span-2 px-2 pb-1 text-xs text-primary">
                      You've picked {CATEGORY_LIMITS[cat]} here — remove one to swap it.
                    </li>
                  )}
                  {items.map((p) => {
                    const on = selected.includes(p.id);
                    const blocked = !on && pickBlock(selected, p.id) !== null;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => toggle(p.id)}
                          aria-pressed={on}
                          disabled={blocked}
                          className={cn(
                            "w-full min-h-[56px] rounded-2xl border px-4 py-3 text-left",
                            "flex items-start gap-3 transition-colors",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                            on
                              ? "border-primary bg-primary/10"
                              : "border-border bg-background hover:border-primary/40",
                            blocked && "opacity-40 cursor-not-allowed",
                          )}
                        >
                          <span
                            className={cn(
                              "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border",
                              on ? "border-primary bg-primary text-primary-foreground" : "border-border",
                            )}
                            aria-hidden="true"
                          >
                            {on && <Check className="h-3 w-3" />}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">{p.label}</span>
                            <span className="block text-xs text-muted-foreground">
                              {p.description}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </motion.ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Honoured, not scored as a midpoint. */}
      <button
        type="button"
        onClick={() => {
          setDraft((prev) => ({ none: !prev.none, selected: [] }));
        }}
        aria-pressed={none}
        className={cn(
          "mt-4 w-full min-h-[56px] rounded-2xl border px-5 py-4 text-left",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          none ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40",
        )}
      >
        <span className="block text-sm font-medium">Nothing here is really me</span>
        <span className="block text-xs text-muted-foreground">
          We'll go on your decisions alone. Your results stay just as complete.
        </span>
      </button>

      <div className="mt-8">
        <button
          onClick={commit}
          disabled={!canContinue}
          className="min-h-[52px] w-full sm:w-auto px-7 rounded-2xl bg-primary text-primary-foreground
            font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40
            focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        {!canContinue && (
          <p className="mt-3 text-xs text-muted-foreground">
            Pick at least one, or choose "Nothing here is really me".
          </p>
        )}
      </div>
    </div>
  );
}
